/**
 * Specialized Loggers for Different Application Domains
 * Provides context-aware logging with data sanitization
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { logger, type LogContext } from './logger';

// Utility function to sanitize sensitive authentication data
function sanitizeAuthData(data: any): any {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const sanitized = { ...data };

  // Remove sensitive fields
  const sensitiveFields = ['password', 'token', 'key', 'secret', 'auth', 'session'];

  Object.keys(sanitized).forEach(key => {
    const lowerKey = key.toLowerCase();
    if (sensitiveFields.some(field => lowerKey.includes(field))) {
      sanitized[key] = '[REDACTED]';
    }
  });

  return sanitized;
}

// Authentication and Security Logger
export const authLogger = {
  error: (message: string, error?: any, metadata?: Record<string, any>) => {
    const context: LogContext = {
      component: 'AUTH',
      metadata: sanitizeAuthData(metadata)
    };
    logger.error(message, sanitizeAuthData(error), context);
  },

  warn: (message: string, data?: any, metadata?: Record<string, any>) => {
    const context: LogContext = {
      component: 'AUTH',
      metadata: sanitizeAuthData(metadata)
    };
    logger.warn(message, sanitizeAuthData(data), context);
  },

  info: (message: string, data?: any) => {
    const context: LogContext = { component: 'AUTH' };
    logger.info(message, sanitizeAuthData(data), context);
  },

  debug: (message: string, data?: any) => {
    const context: LogContext = { component: 'AUTH' };
    logger.debug(message, sanitizeAuthData(data), context);
  }
};

// Operations Logger for Business Logic
export const operationsLogger = {
  error: (operation: string, error: any, metadata?: Record<string, any>) => {
    const context: LogContext = {
      operation,
      ...(metadata && { metadata })
    };
    logger.error(`Operation failed: ${operation}`, error, context);
  },

  warn: (operation: string, message: string, data?: any) => {
    const context: LogContext = { operation };
    logger.warn(message, data, context);
  },

  info: (operation: string, message: string, data?: any) => {
    const context: LogContext = { operation };
    logger.info(message, data, context);
  },

  debug: (operation: string, message: string, data?: any) => {
    const context: LogContext = { operation };
    logger.debug(message, data, context);
  },

  // Utility for timing operations
  time: (operation: string) => {
    return logger.time(operation);
  }
};

// Component Logger for UI Debugging
export const componentLogger = {
  error: (component: string, message: string, error?: any) => {
    const context: LogContext = { component };
    logger.error(message, error, context);
  },

  warn: (component: string, message: string, data?: any) => {
    const context: LogContext = { component };
    logger.warn(message, data, context);
  },

  info: (component: string, message: string, data?: any) => {
    const context: LogContext = { component };
    logger.info(message, data, context);
  },

  debug: (component: string, message: string, data?: any) => {
    const context: LogContext = { component };
    logger.debug(message, data, context);
  }
};

// System Logger for library/system operations
export const systemLogger = {
  error: (message: string, error?: any, metadata?: Record<string, any>) => {
    const context: LogContext = {
      component: 'SYSTEM',
      ...(metadata && { metadata })
    };
    logger.error(message, error, context);
  },

  warn: (message: string, data?: any, metadata?: Record<string, any>) => {
    const context: LogContext = {
      component: 'SYSTEM',
      ...(metadata && { metadata })
    };
    logger.warn(message, data, context);
  },

  info: (message: string, data?: any) => {
    const context: LogContext = { component: 'SYSTEM' };
    logger.info(message, data, context);
  },

  debug: (message: string, data?: any) => {
    const context: LogContext = { component: 'SYSTEM' };
    logger.debug(message, data, context);
  }
};

// Performance Logger for monitoring
export const performanceLogger = {
  mark: (label: string) => {
    return logger.time(label);
  },

  warn: (message: string, metrics?: Record<string, number>) => {
    const context: LogContext = {
      component: 'PERFORMANCE',
      ...(metrics && { metadata: metrics })
    };
    logger.warn(message, metrics, context);
  }
};

// Database Operations Logger
export const dbLogger = {
  error: (query: string, error: any) => {
    const context: LogContext = {
      component: 'DATABASE',
      operation: query
    };
    logger.error('Database operation failed', error, context);
  },

  warn: (message: string, queryInfo?: any) => {
    const context: LogContext = { component: 'DATABASE' };
    logger.warn(message, queryInfo, context);
  },

  debug: (query: string, result?: any) => {
    const context: LogContext = {
      component: 'DATABASE',
      operation: query
    };
    logger.debug('Query executed', result, context);
  }
};